import {
  TypContractManagementTab,
  TypPayStatementTab,
  TypeContract,
  TypePayStatementType,
  TypeUserContract,
} from '../types';

/* 앱 버전 정보 */
export const VERSION = String(process.env.npm_package_version);

/** 앱 환경 정보 */
export const ENVIRONMENT = process.env.ENVIRONMENT;

/** @description User-Agent 개발/디버깅 모드 토큰  */
export const USER_AGENT_DEV_MODE_TOKEN = process.env.USER_AGENT_DEV_MODE_TOKEN;

/** @description 센터별 가입 안내 가이드 URL */
export const CENTER_GUIDE_URL: { [key: string]: string } = Object.freeze({
  CC02: 'https://sites.google.com/view/kurlygimpo/',
  CC03: 'https://sites.google.com/view/kurlypyeongtaek/',
  CC04: 'https://sites.google.com/kurlycorp.com/kurlychw/',
});

/** @description 아르바이트 근무 신청 폼 URL */
export const APPLY_WORK_FORM_URL = 'https://kurly.it/kurly-fulfillment-apply';

/** @description 개인정보 처리방침 URL */
export const PERSONAL_INFO_POLICY_URL = 'https://privacy.kurly.com/kurly/privacypolicy/kurlyro';

/** @description 휴대폰 번호, 인증코드, 관리자코드 등 제한 길이 */
export const PHONE_NUMBER_LENGTH_LIMIT = 11;
export const VERIFICATION_CODE_LENGTH_LIMIT = 6;
export const ADMIN_CODE_LENGTH_LIMIT = 6;
export const PASSWORD_LENGTH_LIMIT = 15;
export const NAME_LENGTH_LIMIT = 10;
export const ID_LENGTH_LIMIT = 15;
export const IDENTIFICATION_NUMBER_LENGTH_LIMIT = 14;
export const DETAIL_ADDRESS_LENGTH_LIMIT = 50;
export const ACCOUNT_NUMBER_LENGTH_LIMIT = 50;
export const REQUEST_REASON_LENGTH_LIMIT = 60;

/** @description 인증코드 유효시간(초) */
export const VERIFICATION_CODE_VALID_SECOND = 180;
/** @description 인증코드 발송 후 재요청 불가시간(초) */
export const VERIFICATION_CODE_CANNOT_RESENT_SECOND = 60;

/** @description dayjs 날짜 표시 형식 */
export const DATE_FORMAT_DEFAULT = 'YYYY-MM-DD';
export const DATE_FORMAT_TO_MONTH = 'YYYY-MM';
export const DATE_FORMAT_TO_MINUTE = 'YYYY-MM-DD HH:mm';
export const DATE_FORMAT_TO_SECOND = 'YYYY-MM-DD HH:mm:ss';
export const DATE_FORMAT_WITH_DAY_OF_WEEK = 'YYYY-MM-DD(ddd) HH:mm';

/** @description 날짜 관련 상수 */
export const DATE_RANGE_MIN_DATE = '2024-01-01';
export const DATE_RANGE_MAX_DATE = '2099-12-31';

/** @description 계약 구분 타입 상수 정의 */
export const CONTRACT_TYPE: {
  [key: string]: TypeContract;
} = {
  CONTRACT: 'CONTRACT',
  SHORT: 'SHORT',
};

/** @description 근로계약 상태 타입 상수 정의 */
export const CONTRACT_STATUS_TYPE: {
  [key: string]: TypeUserContract;
} = {
  FIRST: 'FIRST',
  RENEWAL: 'RENEWAL',
  CURRENT: 'CURRENT',
  UNKNOWN: 'UNKNOWN',
};

/** @description 특수건강검진 상태 코드 */
export const SPECIAL_MEDICAL_EXAMINATION_STATUS_CODE = {
  /** 등록 대기 */
  REGISTRATION_PENDED: 'REGISTRATION_PENDED',
  /** 등록 */
  REGISTERED: 'REGISTERED',
  /** 승인 대기 */
  APPROVAL_PENDED: 'APPROVAL_PENDED',
  /** 승인 */
  APPROVED: 'APPROVED',
  /** 반려 */
  REJECTED: 'REJECTED',
  /** 2차 > 등록 */
  SECOND_REGISTERED: 'SECOND_REGISTERED',
  /** 2차 > 승인 대기 */
  SECOND_APPROVAL_PENDED: 'SECOND_APPROVAL_PENDED',
  /** 2차 > 승인 */
  SECOND_APPROVED: 'SECOND_APPROVED',
  /** 2차 > 반려 */
  SECOND_REJECTED: 'SECOND_REJECTED',
};

export const SPECIAL_MEDICAL_EXAMINATION_STATUS_LABEL = {
  [SPECIAL_MEDICAL_EXAMINATION_STATUS_CODE.REGISTERED]: '제출 필요',
  [SPECIAL_MEDICAL_EXAMINATION_STATUS_CODE.APPROVAL_PENDED]: '승인 대기중',
  [SPECIAL_MEDICAL_EXAMINATION_STATUS_CODE.APPROVED]: '승인',
  [SPECIAL_MEDICAL_EXAMINATION_STATUS_CODE.REJECTED]: '반려',
  [SPECIAL_MEDICAL_EXAMINATION_STATUS_CODE.SECOND_REGISTERED]: '2차 제출 필요',
  [SPECIAL_MEDICAL_EXAMINATION_STATUS_CODE.SECOND_APPROVAL_PENDED]: '2차 승인 대기중',
  [SPECIAL_MEDICAL_EXAMINATION_STATUS_CODE.SECOND_APPROVED]: '2차 승인',
  [SPECIAL_MEDICAL_EXAMINATION_STATUS_CODE.SECOND_REJECTED]: '2차 반려',
};

/** @description 제출 상태 코드 */
export const SUBMISSION_STATUS_CODE = {
  REQUIRED: 'REQUIRED',
  COMPLETED: 'COMPLETED',
  UNAVAILABLE: 'UNAVAILABLE',
};

/** @description 카메라 촬영 에러코드 */
export const CAMERA_RESULT_ERROR_CODE = {
  /** 사진 용량 초과 */
  IMAGE_SIZE_LARGE_ERROR: 'IMAGE_SIZE_LARGE_ERROR',
  /** 사진 로드 실패 */
  IMAGE_LOAD_ERROR: 'IMAGE_LOAD_ERROR',
};

/** @description 계약서 관리 메뉴 > 탭 구분 코드 */
export const CONTRACT_MANAGEMENT_TAB_CODE: { [key: string]: TypContractManagementTab } = Object.freeze({
  /** 완료 계약 */
  COMPLETED: 'COMPLETED',
  /** 취소 계약 */
  CANCELED: 'CANCELED',
});

/** @description 근로계약서 메뉴 > 탭 구분 코드 */
export const PAY_STATEMENT_TAB_CODE: { [key: string]: TypPayStatementTab } = Object.freeze({
  /** 일급 */
  PER_DIEM: 'PER_DIEM',
  /** 기타 */
  ETC: 'ETC',
  /** 월별 */
  MONTHLY: 'MONTHLY',
});

export const PAY_STATEMENT_TYPE_CODE: { [key: string]: TypePayStatementType } = Object.freeze({
  /** 일급 */
  PER_DIEM: 'PER_DIEM',
  /** 주휴 */
  HOLIDAY: 'HOLIDAY',
  /** 특근 */
  SPECIAL_PRO: 'SPECIAL_PRO',
  /** 연차 */
  ANNUAL: 'ANNUAL',
  /** 월별 */
  MONTHLY: 'MONTHLY',
});

/** @description 급여명세서 타입 별 라벨 */
export const PAY_STATEMENT_TYPE_LABEL: { [key: string]: string } = Object.freeze({
  PER_DIEM: '급여명세서(일급)',
  HOLIDAY: '급여명세서(주휴수당)',
  SPECIAL_PRO: '급여명세서(특근비)',
  ANNUAL: '급여명세서(연차수당)',
  MONTHLY: '월별내역서',
});

/** @description 연장근무 희망 여부 옵션 */
export const WISH_OVER_WORK_OPTIONS = [
  { value: 'WISHED', text: '희망' },
  { value: 'NOT_WISHED', text: '비희망' },
];
